import type { CSSProperties } from "react";

import { cn } from "@/lib/utils";
import { CarrierLogo } from "@/components/common/CarrierLogo";
import type { ScheduleEvent, WeekDay } from "@/types/schedule";
import { EVENT_STYLES } from "./ScheduleLegend";

const SLOT_HEIGHT = 2.75;

interface ScheduleWeekGridProps {
  events: ScheduleEvent[];
  timeSlots: string[];
  /** Column headers (Mon–Sun) for the visible week. */
  weekDays: WeekDay[];
  /** Left gutter caption above the time column. */
  gutterLabel?: string;
  className?: string;
  onSelect?: (event: ScheduleEvent) => void;
  selectedId?: string | null;
  /**
   * Drop the fixed 720px min-width and time gutter so the grid can sit in a
   * Home tile. The full schedule page keeps the wide layout.
   */
  compact?: boolean;
}

export function ScheduleWeekGrid({
  events,
  timeSlots,
  weekDays,
  gutterLabel = "GMT+2",
  className,
  onSelect,
  selectedId,
  compact = false,
}: ScheduleWeekGridProps) {
  const hasHourLabels = timeSlots.some((slot) => slot.trim().length > 0);
  const hideTimeGutter = compact || !hasHourLabels;
  const columns = hideTimeGutter
    ? "repeat(7, minmax(0, 1fr))"
    : "3.75rem repeat(7, minmax(0, 1fr))";
  const eventColumnOffset = hideTimeGutter ? 1 : 2;

  return (
    <div className={cn(!hideTimeGutter && "min-w-[720px]", className)}>
      <div
        className="glass-sticky-head list-sticky-head grid border-b border-border/60 pb-2"
        style={{ gridTemplateColumns: columns }}
      >
        {!hideTimeGutter && (
          <div className="text-[10px] text-muted-foreground">{gutterLabel}</div>
        )}
        {weekDays.map((d) => (
          <div key={d.label} className="px-1 text-center">
            <p className="text-xs font-medium">{d.date}</p>
            <p className="text-[10px] uppercase text-muted-foreground">{d.label}</p>
          </div>
        ))}
      </div>

      <div
        className="relative grid"
        style={{
          gridTemplateColumns: columns,
          gridTemplateRows: `repeat(${timeSlots.length}, ${SLOT_HEIGHT}rem)`,
        }}
      >
        {!hideTimeGutter &&
          timeSlots.map((slot, rowIdx) => (
            <div key={rowIdx} style={{ gridColumn: 1, gridRow: rowIdx + 1 }}>
              <span className="text-[10px] text-muted-foreground">{slot}</span>
            </div>
          ))}
        {timeSlots.map((_slot, rowIdx) =>
          weekDays.map((_, colIdx) => (
            <div
              key={`${rowIdx}-${colIdx}`}
              className="border-t border-border/30"
              style={{ gridColumn: colIdx + eventColumnOffset, gridRow: rowIdx + 1 }}
            />
          )),
        )}

        {events.map((ev, index) => (
          <button
            key={ev.id}
            type="button"
            onClick={() => onSelect?.(ev)}
            className={cn(
              "schedule-event-pop m-0.5 overflow-hidden rounded-lg border p-1.5 text-left transition-shadow",
              EVENT_STYLES[ev.kind],
              selectedId === ev.id && "ring-2 ring-primary/50 ring-offset-1 ring-offset-transparent",
              onSelect && "cursor-pointer hover:brightness-105",
            )}
            style={{
              gridColumn: ev.day + eventColumnOffset,
              gridRow: `${ev.startSlot + 1} / span ${ev.span}`,
              "--schedule-pop-index": index,
            } as CSSProperties}
          >
            <div className="flex items-center gap-1">
              <CarrierLogo carrier={ev.carrier} logoOnly size={12} />
              <p className="min-w-0 flex-1 truncate text-[11px] font-medium leading-tight">
                {ev.title}
              </p>
              {ev.count && ev.count > 1 && (
                <span className="shrink-0 rounded-full bg-background/60 px-1.5 text-[9px] font-semibold tabular-nums">
                  {ev.count}
                </span>
              )}
            </div>
            {ev.hasTime && (
              <p className="truncate text-[10px] opacity-80">{ev.time}</p>
            )}
            {!compact && (
              <p className="mt-0.5 truncate text-[10px] opacity-70">{ev.lane}</p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
